import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import { Calculator, Loader2 } from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'jasa', label: 'Jasa Profesional' },
  { value: 'fnb', label: 'F&B / Restoran' },
  { value: 'teknologi', label: 'Teknologi / SaaS' },
  { value: 'pendidikan', label: 'Pendidikan' },
  { value: 'kesehatan', label: 'Kesehatan' },
  { value: 'lainnya', label: 'Lainnya' },
];

function formatRupiah(num) {
  if (num >= 1000000000) return `Rp ${(num / 1000000000).toFixed(1)}M`;
  if (num >= 1000000) return `Rp ${(num / 1000000).toFixed(1)}Jt`;
  if (num >= 1000) return `Rp ${(num / 1000).toFixed(0)}K`;
  return `Rp ${num}`;
}

export default function ROICalculator() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [form, setForm] = useState({
    industry: 'ecommerce',
    monthly_visitors: 5000,
    current_conversion: 2,
    avg_transaction_value: 500000,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/roi`, form);
      setResult(res.data?.data || res.data);
    } catch {
      setResult({
        current_revenue: form.monthly_visitors * (form.current_conversion / 100) * form.avg_transaction_value,
        projected_revenue: form.monthly_visitors * (form.current_conversion * 2.2 / 100) * form.avg_transaction_value,
        revenue_increase: form.monthly_visitors * (form.current_conversion * 1.2 / 100) * form.avg_transaction_value,
        cost_savings: form.monthly_visitors * (form.current_conversion / 100) * form.avg_transaction_value * 0.15,
        payback_months: 6,
      });
    }
    setLoading(false);
  };

  const chartData = result ? [
    { name: 'Sekarang', value: result.current_revenue },
    { name: 'Proyeksi', value: result.projected_revenue },
  ] : [];

  return (
    <section
      data-testid="roi-section"
      ref={ref}
      style={{ backgroundColor: theme === 'dark' ? '#0C1120' : '#F0EDE8' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <motion.span
          className="font-mono text-xs tracking-[0.2em] uppercase block mb-4"
          style={{ color: 'var(--accent-primary)' }}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
        >
          ROI Calculator
        </motion.span>
        <motion.h2
          className="font-heading text-3xl md:text-5xl font-semibold tracking-tight max-w-3xl mb-16"
          style={{ color: 'var(--text-primary)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
        >
          Hitung potensi ROI website Anda
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="glass-card p-8 space-y-6"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            data-testid="roi-form"
          >
            <div>
              <label className="font-mono text-xs tracking-wider uppercase block mb-2" style={{ color: 'var(--text-secondary)' }}>Industri</label>
              <Select
                value={form.industry}
                onValueChange={val => setForm({ ...form, industry: val })}
              >
                <SelectTrigger data-testid="roi-industry">
                  <SelectValue placeholder="Pilih industri..." />
                </SelectTrigger>
                <SelectContent>
                  {industries.map(ind => (
                    <SelectItem key={ind.value} value={ind.value}>
                      {ind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="font-mono text-xs tracking-wider uppercase block mb-2" style={{ color: 'var(--text-secondary)' }}>Pengunjung / Bulan</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.monthly_visitors}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '');
                  setForm({ ...form, monthly_visitors: parseInt(val) || 0 });
                }}
                data-testid="roi-visitors"
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  border: '1px solid var(--border-card)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            <div>
              <label className="font-mono text-xs tracking-wider uppercase flex justify-between mb-2" style={{ color: 'var(--text-secondary)' }}>
                <span>Conversion Rate Saat Ini</span>
                <span style={{ color: 'var(--accent-primary)' }}>{form.current_conversion}%</span>
              </label>
              <input
                type="range"
                min="0.1"
                max="15"
                step="0.1"
                value={form.current_conversion}
                onChange={e => setForm({ ...form, current_conversion: parseFloat(e.target.value) })}
                data-testid="roi-conversion"
                className="w-full"
                style={{ accentColor: 'var(--accent-primary)' }}
              />
            </div>

            <div>
              <label className="font-mono text-xs tracking-wider uppercase block mb-2" style={{ color: 'var(--text-secondary)' }}>Rata-rata Nilai Transaksi (Rp)</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.avg_transaction_value}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '');
                  setForm({ ...form, avg_transaction_value: parseInt(val) || 0 });
                }}
                data-testid="roi-transaction"
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  border: '1px solid var(--border-card)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="roi-submit"
              className="w-full py-4 rounded-xl font-heading font-semibold text-sm tracking-wide uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: 'var(--accent-primary)',
                color: theme === 'dark' ? '#0D0D0D' : '#FFFFFF',
              }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />}
              Hitung ROI
            </button>
          </motion.form>

          {/* Result */}
          <motion.div
            className="glass-card p-8"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            data-testid="roi-result"
          >
            {result ? (
              <div className="space-y-6">
                <h3 className="font-heading text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Proyeksi Hasil</h3>

                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} barSize={60}>
                      <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => formatRupiah(v)} />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {chartData.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? (theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)') : 'var(--accent-primary)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl" style={{ background: theme === 'dark' ? 'rgba(0,255,133,0.05)' : 'rgba(13,148,136,0.05)' }}>
                    <span className="font-mono text-[10px] tracking-wider uppercase block mb-1" style={{ color: 'var(--text-secondary)' }}>Peningkatan Revenue</span>
                    <span className="font-heading text-xl font-bold" style={{ color: 'var(--accent-primary)' }}>{formatRupiah(result.revenue_increase)}</span>
                    <span className="text-xs block" style={{ color: 'var(--text-secondary)' }}>/bulan</span>
                  </div>
                  <div className="p-4 rounded-xl" style={{ background: theme === 'dark' ? 'rgba(58,130,255,0.05)' : 'rgba(37,99,235,0.05)' }}>
                    <span className="font-mono text-[10px] tracking-wider uppercase block mb-1" style={{ color: 'var(--text-secondary)' }}>Penghematan</span>
                    <span className="font-heading text-xl font-bold" style={{ color: 'var(--accent-secondary)' }}>{formatRupiah(result.cost_savings)}</span>
                    <span className="text-xs block" style={{ color: 'var(--text-secondary)' }}>/bulan</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl text-center" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                  <span className="font-mono text-[10px] tracking-wider uppercase block mb-1" style={{ color: 'var(--text-secondary)' }}>Timeline Payback</span>
                  <span className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{result.payback_months} Bulan</span>
                </div>

                <a
                  href="https://wa.me/6281999934451?text=Halo%20Bewhy,%20saya%20ingin%20jadwalkan%20Sesi%20Discovery"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="roi-cta"
                  className="block w-full py-3 rounded-xl text-center font-heading font-semibold text-sm tracking-wide uppercase transition-all hover:-translate-y-0.5"
                  style={{ border: '1px solid var(--accent-primary)', color: 'var(--accent-primary)' }}
                >
                  Ingin hasil ini? Jadwalkan Sesi Discovery
                </a>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 min-h-[300px]">
                <Calculator size={48} style={{ color: 'var(--text-secondary)', opacity: 0.3 }} />
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Isi form di samping untuk melihat proyeksi ROI website Anda
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
